import { ReactNode, useState } from 'react';
import { CaretRightIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { ExpandableChildren } from './ExpandableChildren';
import { ListItem, ListItemProps } from './ListItem';

export interface ExpandableListItemProps extends Omit<
    ListItemProps,
    'onClick'
> {
    children?: ReactNode;
    expanded?: boolean;
    defaultExpanded?: boolean;
    onExpandedChange?: (expanded: boolean) => void;
    onClick?: () => void;
    childrenWrapperClassName?: string;
    chevronClassName?: string;
    childrenMaxHeightClass?: string;
}

export function ExpandableListItem({
    children,
    expanded: controlledExpanded,
    defaultExpanded = false,
    onExpandedChange,
    onClick,
    childrenWrapperClassName,
    chevronClassName,
    childrenMaxHeightClass,
    startContent,
    ...listItemProps
}: ExpandableListItemProps) {
    const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
    const isControlled = controlledExpanded !== undefined;
    const expanded = isControlled ? controlledExpanded : internalExpanded;
    const hasChildren = children !== undefined && children !== null;

    const setExpanded = (next: boolean) => {
        if (!isControlled) setInternalExpanded(next);
        onExpandedChange?.(next);
    };

    const handleRowClick = () => {
        if (hasChildren && !expanded) setExpanded(true);
        onClick?.();
    };

    const handleChevronClick = (event: React.MouseEvent) => {
        if (!hasChildren) return;
        event.stopPropagation();
        setExpanded(!expanded);
    };

    const handleChevronKeyDown = (event: React.KeyboardEvent) => {
        if (!hasChildren) return;
        if (event.key === 'Enter' || event.key === ' ') {
            event.stopPropagation();
            event.preventDefault();
            setExpanded(!expanded);
        }
    };

    const enrichedStartContent = (
        <>
            <span
                role={hasChildren ? 'button' : undefined}
                tabIndex={hasChildren ? 0 : -1}
                aria-label={expanded ? 'Collapse' : 'Expand'}
                onClick={handleChevronClick}
                onKeyDown={handleChevronKeyDown}
                className={cn(
                    'flex size-4 shrink-0 items-center justify-center rounded',
                    hasChildren &&
                        'cursor-pointer hover:bg-grey-500 hover:text-white-100',
                )}
            >
                <CaretRightIcon
                    className={cn(
                        'size-3 shrink-0 text-grey-200 transition-transform duration-150',
                        expanded && 'rotate-90',
                        !hasChildren && 'invisible',
                        chevronClassName,
                    )}
                    weight="bold"
                    aria-hidden
                />
            </span>
            {startContent}
        </>
    );

    return (
        <div className="flex w-full flex-col">
            <ListItem
                {...listItemProps}
                startContent={enrichedStartContent}
                onClick={handleRowClick}
                aria-expanded={hasChildren ? expanded : undefined}
            />
            {hasChildren && (
                <div
                    className={cn(
                        'grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none',
                        expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                    )}
                    inert={!expanded}
                >
                    <div className="overflow-hidden">
                        <ExpandableChildren
                            maxHeightClass={childrenMaxHeightClass}
                            className={childrenWrapperClassName}
                        >
                            {children}
                        </ExpandableChildren>
                    </div>
                </div>
            )}
        </div>
    );
}
